import React, { useState, useEffect } from 'react'
import { Box, Flex, Group, Button, Radio, CheckIcon, Image, Text, Input, Modal, ScrollArea, TextInput } from '@mantine/core'
import { getPaymentChannelList } from '../../../../../services/payment-channels'
import { useSelector, useDispatch } from 'react-redux'
import { updateOrder } from '../../../../../services/orders'
import { getOrderDetail } from '../../../../../store/orderDetail'
import { notificationSuccess, notificationError } from '../../../../ui/Notifications'
import { useParams } from 'react-router-dom'
import LoadingData from '../../../../ui/LoadingData'
import { CKEditor } from '@ckeditor/ckeditor5-react'
import { 
  ClassicEditor, 
  Bold, 
  Essentials, 
  Italic, 
  Mention, 
  Paragraph, 
  Undo, 
  Font, 
  FontSize,
  Heading,
  List,
  Alignment,
  Link,
  PasteFromOffice
} from 'ckeditor5'

const defaultVal = {
  payment_channel_id: null,
  instruction: '',
  payment_link: ''
}

const FormAdjustPaymentMethod = ({ onCloseForm }) => {
  const dispatch = useDispatch()
  const { paymentMethods } = useSelector(state => state.order_detail)
  const params = useParams()
  const orderId = params.order_id
  const [loadingPayment, setLoadingPayment] = useState(true)
  const [loadingForm, setLoadingForm] = useState(false)
  const [paymentMethodList, setPaymentMethodList] = useState([])
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState([])
  const [openFormCustomInstruction, setOpenFormCustomInstruction] = useState(false)
  const [openFormPaymentLink, setOpenFormPaymentLink] = useState(false)
  const [formData, setFormData] = useState(defaultVal)
  const [isLoaded, setIsLoaded] = useState(false)

  const submitUpdatePaymentOrder = async () => {
    setLoadingForm(true)
    const payload = {
      order_channels: selectedPaymentMethod
    }
    try {
      const response = await updateOrder(orderId, payload)
      if (response.success) {
        onCloseForm()
        dispatch(getOrderDetail(orderId))
        notificationSuccess('Update Order Successfully', 'This data has been updated')
      }
    } catch (error) {
      console.log(error)
      const errorMessage = error.response.data.message
      notificationError('Update Order Error', `${errorMessage ? errorMessage : 'Please recheck your form'}`)
    } finally {
      setLoadingForm(false)
    }
  }

  const configEditor = {
    plugins:[
      Bold,
      Essentials,
      Italic,
      Mention,
      Paragraph,
      Undo,
      Font,
      FontSize,
      Heading,
      List,
      Alignment,
      Link,
      PasteFromOffice
    ],
    alignment: {
      options: [ 'left', 'right' ]
    },
    fontSize: {
      options: [
          9,
          10,
          11,
          12,
          13,
          14,
          15,
          16,
          17,
          18,
          19,
          20,
          21,
          22
      ],
      supportAllValues: true
    },
    list: {
      properties: {
        styles: true,
        startIndex: true,
        reversed: true
      }
    },
    link: {
      decorators: {
        addTargetToExternalLinks: {
          mode: 'automatic',
          callback: url => /^(https?:)?\/\//.test( url ),
          attributes: {
              target: '_blank',
              rel: 'noopener noreferrer'
          }
        }
      }
    }, 
    toolbar: {
      items: [
        'undo', 'redo',
        '|',
        'heading',
        '|',
        'fontSize', 'Bold', 'Italic', 'alignment',
        '|',
        'link',
        '|',
        'bulletedList', 'numberedList',
        '|',
      ],
      shouldNotGroupWhenFull: true
    },
    initialData: `${formData.instruction}`
  }

  const initCKeditor = () => {
    return (
      <CKEditor
        editor={ClassicEditor}
        config={configEditor}
        data={formData.instruction}
        onReady={(editor) => {
          editor.execute('fontSize', { value: '12px' })
          editor.editing.view.change((writer) => {
            writer.setStyle(
              'height',
              '400px',
              editor.editing.view.document.getRoot()
            )
          })
        }}
        onChange={( event, editor ) => {
          editor.execute('fontSize', { value: '12px' })
          const valueEditor = editor.getData()
          handleChangeForm('instruction', valueEditor)
        }}
      />
    )
  }


  const handleGetPaymentChannelList = async () => {
    setLoadingPayment(true)
    try {
      const response = await getPaymentChannelList({ 'page[limit]': 100 })
      if (response.success && response.data) {
        const dataPaymentChannel = response.data
        const remapData = dataPaymentChannel.map((val) => {
          return {
            id: val.id || val.attributes?.id,
            ...val.attributes
          }
        })
        
        setPaymentMethodList(remapData)
      }
    } catch (error) {
      console.log(error)
    } finally {
      setLoadingPayment(false)
    }
  }

  useEffect(() => {
    handleGetPaymentChannelList()
  }, [])

  useEffect(() => {
    handleGetSelectedPayment(paymentMethods)
    // eslint-disable-next-line
  }, [])

  const handleGetSelectedPayment = (recentPayment) => {
    if (recentPayment.length > 0) {
      const remapPayment = recentPayment.map((val) => {
        return {
          payment_channel_id: val.payment_channel_id,
          instruction: val.instruction ? val.instruction : '',
          payment_link: val.payment_link
        }
      })
      setSelectedPaymentMethod(remapPayment)
    }
  }

  const handleSelectedPayment = (valueId) => {
    const findSelectedData = selectedPaymentMethod.find((val) => val.payment_channel_id === valueId)
    if (findSelectedData) {
      const filterData = selectedPaymentMethod.filter((val) => val.payment_channel_id !== valueId)
      setSelectedPaymentMethod(filterData)
    } else {
      const selectedData = paymentMethodList.find((val) => val.id === valueId)
      const payload = {
        payment_channel_id: selectedData.id,
        instruction: selectedData.instruction ? selectedData.instruction : '',
        payment_link: selectedData.payment_link
      }
      setSelectedPaymentMethod(oldVal => [...oldVal, payload])
    }
  }

  const handleSelectedData = (value) => {
    const selectedData = selectedPaymentMethod.find((val) => val.payment_channel_id === value)
    return selectedData ? true : false
  }

  const handleAssignFormInstruction = (event, valueId) => {
    event.stopPropagation()
    const selectedData = selectedPaymentMethod.find((val) => val.payment_channel_id === valueId)
    setFormData(selectedData)
    setOpenFormCustomInstruction(true)
    setIsLoaded(true)
  }

  const handleAssignFormPaymentLink = (event, valueId) => {
    event.stopPropagation()
    const selectedData = selectedPaymentMethod.find((val) => val.payment_channel_id === valueId)
    setFormData(selectedData)
    setOpenFormPaymentLink(true)
  }

  const onCloseFormInstruction = () => {
    setFormData(defaultVal)
    setOpenFormCustomInstruction(false)
    setIsLoaded(false)
  }

  const onCloseFormPaymentLink = () => {
    setFormData(defaultVal)
    setOpenFormPaymentLink(false)
  }

  const handleChangeForm = (name, val) => {
    setFormData((oldVal) => ({ ...oldVal, [name]: val}))
  }

  const submitUpdatedForm = (form) => {
    const updatedData = selectedPaymentMethod.map((val) => {
      return {
        ...val,
        instruction: val.payment_channel_id === form.payment_channel_id ? form.instruction : val.instruction,
        payment_link: val.payment_channel_id === form.payment_channel_id ? form.payment_link : val.payment_link
      }
    })
    setSelectedPaymentMethod(updatedData)
    onCloseFormPaymentLink()
    onCloseFormInstruction()
    
  }

  const mappingFormPayment = (dataPayment) => {
    const mappingDataPayment = (data) => {
      const remapStatus = data.map((val, index) => {

        return (
          <Radio.Card radius="md" p='sm' mb='sm' checked={handleSelectedData(val.id)} onClick={() => handleSelectedPayment(val.id)} style={{ zIndex: 0 }} >
            <Group wrap="nowrap" justify='space-between' align="center">
              <Group align='center' >
                <Radio.Indicator icon={CheckIcon} />
                <Image src={val.logo_url} w={50} h={50} fit='contain' />
                <Text fz={12} fw='500' tt='capitalize' c='gray.7'>{val.name}</Text>
              </Group>
              <Group align='center' style={{ zIndex: 10 }}>
                {handleSelectedData(val.id) && val.gateway?.value === 'manual' ? <Button size='compact-xs' radius='lg' variant='outline' onClick={(event) => handleAssignFormInstruction(event, val.id)}>Custom Instruction</Button> : ''}
                {handleSelectedData(val.id) && val.gateway?.value === 'manual' ? <Button size='compact-xs' radius='lg' variant='outline' onClick={(event) => handleAssignFormPaymentLink(event, val.id)}>Payment Link</Button> : ''}
              </Group>
            </Group>
          </Radio.Card>
        )
      })

      return remapStatus
    }

    return (
      <Box>
        <Box mb='lg'>
          <Input.Wrapper label="Select Payment method" withAsterisk size='xs'>
            <ScrollArea h={500} my={10} type="auto" offsetScrollbars scrollbarSize={4}>
              {mappingDataPayment(dataPayment)}
            </ScrollArea>
          </Input.Wrapper>
        </Box>       
        <Box mt={30}>
          <Flex justify='flex-end'>
            <Group>
              <Button size='xs' variant="outline" onClick={onCloseForm}>Close</Button>
              <Button size='xs' loading={loadingForm} variant="filled" onClick={() => submitUpdatePaymentOrder()} disabled={selectedPaymentMethod.length === 0}>Update</Button>
            </Group>
          </Flex>
        </Box>
      </Box>
    )
  }

  return (
    <Box>
      {loadingPayment ? <LoadingData /> : mappingFormPayment(paymentMethodList)}
      <Modal opened={openFormCustomInstruction} onClose={onCloseFormInstruction} centered closeOnClickOutside={false} size='xl' title={<Text fw='Bold'>Update Custom Instruction</Text>}  scrollAreaComponent={ScrollArea} withCloseButton={false}>
        <Box>
          <Box mb='md'>
            <Input.Wrapper 
              label="Instruction"
              size='xs'
            >
              {isLoaded ? initCKeditor() : ''}
            </Input.Wrapper>
          </Box>
          <Box mt={20}>
            <Flex justify='flex-end'>
              <Group>
                <Button size='xs' variant="outline" onClick={() => onCloseFormInstruction()}>Close</Button>
                <Button size='xs' variant="filled" onClick={() => submitUpdatedForm(formData)}>Update</Button>
              </Group>
            </Flex>
          </Box>
        </Box>
      </Modal>
      <Modal opened={openFormPaymentLink} onClose={onCloseFormPaymentLink} centered closeOnClickOutside={false} size='lg' title={<Text fw='Bold'>Update Payment Link</Text>}  scrollAreaComponent={ScrollArea} withCloseButton={false}>
        <Box>
          <Box mb='md'>
            <TextInput
              name='payment_link'
              value={formData.payment_link}
              label='Payment Link'
              placeholder='Input payment link'
              onChange={(val) => handleChangeForm('payment_link', val.target.value)}
              size='xs'
            />
          </Box>
          <Box mt={20}>
            <Flex justify='flex-end'>
              <Group>
                <Button size='xs' variant="outline" onClick={() => onCloseFormPaymentLink()}>Close</Button>
                <Button size='xs' variant="filled" onClick={() => submitUpdatedForm(formData)}>Update</Button>
              </Group>
            </Flex>
          </Box>
        </Box>
      </Modal>
    </Box>
  )
}

export default FormAdjustPaymentMethod